import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Router } from '@angular/router';
import { SitesService, Site } from '../sites.service';
import { HasPermissionDirective } from '../../../../core/directives/has-permission.directive';
import { SiteFormDialogComponent } from '../site-form-dialog/site-form-dialog.component';
import { ConfirmationService } from '../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-sites-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  templateUrl: './sites-list.component.html',
  styleUrls: ['./sites-list.component.scss'],
})
export class SitesListComponent implements OnInit, OnDestroy {
  sites: Site[] = [];
  loading = false;
  displayedColumns: string[] = [
    'name',
    'status',
    'actions',
  ];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private sitesService: SitesService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.configurePageHeader();
    this.loadSites();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  private configurePageHeader(): void {
    this.pageHeader.set({
      title: 'Site Management',
      icon: 'store',
      backRoute: '/dashboard',
      actions: [
        {
          label: 'Refresh',
          icon: 'refresh',
          variant: 'stroked',
          permission: 'site.read',
          disabled: () => this.loading,
          handler: () => this.loadSites(),
        },
        {
          label: 'Add Site',
          icon: 'add_location',
          variant: 'primary',
          permission: 'site.create',
          handler: () => this.onCreateSite(),
        },
      ],
    });
  }

  loadSites(): void {
    this.loading = true;
    this.sitesService
      .getSitesPage({ page: this.pageIndex + 1, perPage: this.pageSize })
      .subscribe({
        next: (page) => {
          this.sites = page.items;
          this.total = page.meta.total;
          this.pageSize = page.meta.perPage;
          this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading sites:', error);
          this.snackBar.open('Failed to load sites', 'Close', { duration: 3000 });
          this.loading = false;
        },
      });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadSites();
  }

  onViewSite(site: Site): void {
    this.router.navigate(['/admin/sites', site.id]);
  }

  onCreateSite(): void {
    const dialogRef = this.dialog.open(SiteFormDialogComponent, {
      width: '660px',
      maxHeight: '90vh',
      data: {},
    });
    dialogRef.afterClosed().subscribe((created) => {
      if (created) this.loadSites();
    });
  }

  onEditSite(site: Site): void {
    const dialogRef = this.dialog.open(SiteFormDialogComponent, {
      width: '660px',
      maxHeight: '90vh',
      data: { siteId: site.id },
    });
    dialogRef.afterClosed().subscribe((updated) => {
      if (updated) this.loadSites();
    });
  }

  onManageUsers(site: Site): void {
    this.router.navigate(['/users'], { queryParams: { siteId: site.id } });
  }

  onToggleActive(site: Site): void {
    this.sitesService.toggleActive(site.id).subscribe({
      next: (response) => {
        if (response.success) {
          site.is_active = !site.is_active;
          this.snackBar.open(
            `Site ${site.is_active ? 'activated' : 'deactivated'} successfully`,
            'Close',
            { duration: 3000 },
          );
        }
      },
      error: (error) => {
        console.error('Error toggling site status:', error);
        this.snackBar.open('Failed to update site status', 'Close', {
          duration: 3000,
        });
      },
    });
  }

  onDeleteSite(site: Site): void {
    this.confirmationService.confirm({
      title: 'Delete site',
      message: `Are you sure you want to delete "${site.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.sitesService.deleteSite(site.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.snackBar.open('Site deleted successfully', 'Close', {
              duration: 3000,
            });
            this.loadSites();
          }
        },
        error: (error) => {
          console.error('Error deleting site:', error);
          const message = error.error?.message || 'Failed to delete site';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
    });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
